import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { PaywallSheet } from './PaywallSheet';

export type PaywallReason =
  /** User hit the free monthly OCR scan cap (5/month). */
  | 'scan_cap'
  /** User opted in from Settings or another call-to-action. */
  | 'upgrade';

export interface PaywallOpenArgs {
  reason: PaywallReason;
}

interface PaywallContextValue {
  open: (args: PaywallOpenArgs) => void;
  close: () => void;
}

const PaywallContext = createContext<PaywallContextValue | null>(null);

export const PaywallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [args, setArgs] = useState<PaywallOpenArgs | null>(null);

  const open = useCallback((next: PaywallOpenArgs) => setArgs(next), []);
  const close = useCallback(() => setArgs(null), []);

  const value = useMemo<PaywallContextValue>(() => ({ open, close }), [open, close]);

  return (
    <PaywallContext.Provider value={value}>
      {children}
      {args ? <PaywallSheet reason={args.reason} onClose={close} /> : null}
    </PaywallContext.Provider>
  );
};

export function usePaywall(): PaywallContextValue {
  const ctx = useContext(PaywallContext);
  if (!ctx) {
    throw new Error('usePaywall must be used within PaywallProvider');
  }
  return ctx;
}
